export const sentinelContent = {
  hero: {
    title: "Sentinel",
    subtitle: "Real-Time Monitoring, Risk Control and Business Management",
    description:
      "Sentinel is a real-time monitoring, risk control, and business management platform designed for investment accounts across multiple trading technologies.",
  },

  about: {
    title: "ABOUT",
    intro: [
      "Sentinel is a real-time monitoring, risk control, and business management platform designed for investment accounts across multiple trading technologies.",
    ],
    capabilities: [
      "Monitor trading accounts in real time",
      "Apply automatic risk control rules",
      "Manage trading operations as a structured business",
      "Centralize data from multiple brokers and technologies",
    ],
    activeControl:
      "Sentinel is not only an analytics tool. It is an active control system, capable of intervening directly in trading accounts when risk conditions are met.",
    pillars: [
      {
        title: "Risk Control",
        items: [
          "Continuous monitoring of account risk",
          "Automatic closing of positions when limits are reached",
          "Blocking of trades that increase risk",
          "Protection against excessive drawdown",
        ],
      },
      {
        title: "Monitoring & Analytics",
        items: [
          "Real-time account tracking",
          "Persistent trade history stored in database",
          "Operational and financial dashboards",
        ],
      },
      {
        title: "Business Management",
        items: [
          "Investor management (CPA, MAM, etc.)",
          "Fee calculation",
          "Rebate tracking",
          "Subscription management",
          "Investment program management",
        ],
      },
    ],
    efficiency: {
      intro:
        "Sentinel was designed for professional environments where strict control and scalability are required.",
      useCasesTitle: "Typical use cases:",
      useCases: [
        "Asset Managers",
        "Hedge Funds",
        "MAM / PAMM managers",
        "Algorithmic trading firms",
        "Brokers and Introducing Brokers",
        "Signal providers",
        "Investment programs",
      ],
      whenTitle: "Sentinel is especially efficient when:",
      when: [
        "Managing multiple accounts",
        "Running leveraged strategies",
        "Operating across several brokers",
        "Requiring centralized risk control",
        "Running trading as a business",
      ],
    },
    technologies: {
      intro: "Current version supports:",
      current: ["MetaTrader 4 (MT4)", "MetaTrader 5 (MT5)"],
      plannedTitle: "Planned support:",
      planned: ["Interactive Brokers (IBKR)", "FIX 4.4 Protocol"],
      note: "Future versions will allow multi-technology monitoring within the same environment.",
    },
    subscriptionModel: {
      intro:
        "Sentinel works with a subscription model based on the number of active accounts. Each connected account counts as one unit. The system scales automatically as the number of accounts increases.",
      freeTitle: "Free Live Subscription",
      freeIntro: "Sentinel provides a Free Live Subscription, which means:",
      freeItems: [
        "No payment required at activation",
        "Real accounts can be used",
        "Full functionality available",
        "Immediate start",
      ],
      freeNote: "This allows users to test the system in real trading conditions.",
    },
  },

  getStarted: {
    title: "GET STARTED",
    intro: "This section explains how to configure Sentinel for the first time.",
    steps: [
      {
        title: "Create Sentinel account",
        description: "Register your Sentinel account to access the platform.",
      },
      {
        title: "Activate Free Live Subscription",
        description:
          "Start immediately with a free live subscription — no payment required at activation.",
      },
      {
        title: "Add MT4 / MT5 accounts",
        description:
          "Connect your MetaTrader 4 or MetaTrader 5 trading accounts to Sentinel.",
      },
      {
        title: "Define risk parameters",
        description:
          "Set the risk control rules that Sentinel will enforce on your accounts.",
      },
      {
        title: "Validate connection",
        description:
          "Confirm the connection between Sentinel and your trading accounts is active.",
      },
      {
        title: "Start monitoring",
        description:
          "Begin real-time monitoring and automatic risk control of your accounts.",
      },
    ],
  },

  features: {
    title: "FEATURES",
    intro: null,
    items: [
      {
        title: "Real-Time Risk Management",
        description:
          "Sentinel provides active risk control at account level. Define risk limits, automatically close all trades when a limit is reached, block new trades that increase risk, and maintain continuous real-time monitoring. This is the core functionality of Sentinel.",
      },
      {
        title: "Persistent Trade History Database",
        description:
          "All trades are stored in the Sentinel database. History is fully auditable and independent from broker retention limits, enabling long-term analytics and complete audit capability.",
      },
      {
        title: "CPA Account Management",
        description:
          "Monitor CPA accounts with performance tracking per account, volume tracking, profitability analysis, and broker performance evaluation.",
      },
      {
        title: "MAM Account Management",
        description:
          "Sentinel supports MAM structures with master / investor relationship tracking, automatic fee calculation, investor performance reports, and account consolidation.",
      },
      {
        title: "High-Water Mark Calculation (HWM)",
        description:
          "Sentinel automatically calculates High-Water Mark in monthly, quarterly, or custom periods. Used for performance fee calculation, investor reporting, and fund management.",
      },
      {
        title: "Volume Analysis",
        description:
          "Track volume generated per account, broker, investor, and program. Useful for CPA evaluation, rebate calculation, and business analytics.",
      },
      {
        title: "Rebate Management",
        description:
          "Track rebates generated by trading activity per broker, account, and investor. Provides full revenue analysis for business operations.",
      },
      {
        title: "Investment Programs",
        description:
          "Create and manage investment programs by defining signal sources, assigning accounts, tracking performance, and managing investors. Used for signal services, managed accounts, and fund structures.",
      },
      {
        title: "Client Subscriptions",
        description:
          "Manage subscription-based access to signals by creating subscription plans, assigning clients, enabling or disabling access, and tracking active users.",
      },
      {
        title: "Real-Time Dashboards",
        description:
          "Operational dashboards covering risk overview, account performance, volume analytics, fee reports, program statistics, and business metrics. All data updates in real time.",
      },
    ],
  },

  subscription: {
    title: "SUBSCRIPTION",
    items: [
      {
        title: "Subscription Types",
        content:
          "All subscription plans provide the same features and functionality.\n\nSubscriptions can be purchased for four different periods: monthly, quarterly, semi-annual, and annual.\n\nThe minimum number of subscriptions required per purchase is two.",
      },
      {
        title: "Account Limits",
        content:
          "There is no limit to the number of trading accounts that can be managed.\n\nSentinel is designed to support multi-account environments, allowing users to monitor and control risk across any number of accounts from a single Dashboard.",
      },
      {
        title: "Upgrade / Downgrade",
        content:
          "You can increase the number of connected accounts at any time by purchasing additional subscriptions.\n\nEach new purchase requires a minimum of two subscriptions.\n\nTo downgrade, you can cancel existing subscriptions or choose not to renew them. Cancellations take effect at the end of the current billing period and are not applied immediately.",
      },
      {
        title: "Free Live Subscription",
        content:
          "When the service is activated, no payment is required initially, as Sentinel provides one free Live subscription.\n\nIf you only need to use Sentinel with a single trading account, the platform can be used at no cost, since the free Live subscription covers one account.",
      },
      {
        title: "Billing Management",
        content:
          "All subscriptions can be managed from the Subscriptions section, where users can view active plans, renewals, and status.\n\nAll issued invoices are also available in this area, allowing full access to billing history at any time.",
      },
    ],
  },

  faqs: {
    title: "FAQs",
    items: [
      {
        title: "Does Sentinel execute trades?",
        content:
          "Sentinel does not execute trades.\n\nIt is a monitoring and risk-management system designed to supervise trading activity, enforce risk limits, and ensure compliance with predefined rules, without sending or executing orders in the market.",
      },
      {
        title: "What happens when risk limit is reached?",
        content:
          "If the Risk Stop is enabled for the trading account and the master password has been provided, once the defined risk limit is reached all open trades are automatically closed.\n\nAn alert is then generated in the Dashboard, and a notification is sent to inform the user that the risk limit has been triggered.",
      },
      {
        title: "Can I use multiple brokers?",
        content:
          "There is no limit to the number of brokers that can be used.\n\nSentinel is designed to work with multiple brokers simultaneously, allowing users to monitor and manage risk across different trading accounts from a single Dashboard.",
      },
      {
        title: "Is history stored even if broker deletes it?",
        content:
          "Yes. All orders are archived by Sentinel and remain accessible at any time, even if the broker truncates or deletes the trading history.\n\nThis ensures full auditability and allows users to review past activity independently of the broker's historical records.",
      },
      {
        title: "How are fees calculated?",
        content:
          "Management fees and performance fees are calculated according to standard industry practices, based on the percentage values defined in the account configuration.\n\nFee calculations are performed in real time, and the accumulated amount is continuously displayed in the Dashboard, allowing users to see the fees generated up to that moment.",
      },
      {
        title: "Can I manage MAM accounts?",
        content:
          "Yes. Sentinel allows the creation of MAM accounts, where multiple sub-accounts can be linked to a master account.\n\nIt is also possible to assign a dedicated rebate account that will receive the management and performance fees generated by the MAM structure.",
      },
      {
        title: "Can I sell signals using Sentinel?",
        content:
          "Yes. Sentinel allows the creation of investment programs linked to specific trading accounts.\n\nThrough these programs, users can display the signals generated by those accounts and charge a subscription fee for access to the service.",
      },
    ],
  },
};
